import pinIcon from '../../assets/storymap/pin.svg';
import IndexList from './sections/IndexList';
import { useEffect, useMemo, useRef, useState } from 'react';
import ProgramDetailCard from './programs/ProgramDetailCard';
import { PageDots } from './utils/PageDots';
import { showProgramPoint, toggleProgrammaticLayer } from '../../utils/messages';
import { partners } from './utils/partners';
import WorkforceImpactCard from './impactCards/WorkforceImpactCard';
import EnvironmentImpactCard from './impactCards/EnvironmentImpactCard';
import EconomicImpactCard from './impactCards/EconomicImpactCard';
import StorymapFooter from './sections/StorymapFooter';
import { useSearchParams } from 'react-router-dom';
import { TransparentSpacer } from './utils/TransparentSpacer';
import LinkCardGrid from './sections/LinkCardGrid';
import { cards } from './utils/linkCards';

const StorymapPage = () => {
    const detailRef = useRef<HTMLDivElement | null>(null);
    const sectionRefs = useRef<(HTMLElement | null)[]>(new Array(6).fill(null));
    const [searchParams, setSearchParams] = useSearchParams();

    // Context for partners/programs
    const partnerIndexParam = searchParams.get("partnerIndex");
    const partnerIndex = partnerIndexParam ? Number(partnerIndexParam) : null;

    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    const selectedProgram = useMemo(() => {
        if (selectedIndex === null) return null;
        return partners[selectedIndex];
    }, [partners, selectedIndex]);

    // Handler for changing programs
    const handleSelectProgram = (index: number) => {
        if (selectedIndex === index) {
            setSelectedIndex(null);
            return;
        }

        setSelectedIndex(index);
        showProgramPoint(partners[index].item);
    };

    // Effect Observers
    // 1. On normal fresh load, reset to top
    // useEffect(() => {
    //     if (partnerIndexParam) return;

    //     window.history.scrollRestoration = "manual";
    //     window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    //     return () => {
    //         window.history.scrollRestoration = "auto";
    //     };
    // }, [partnerIndexParam]);

    // 2. Intersection observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;

                    if (entry.target === sectionRefs.current[0]) {
                        toggleProgrammaticLayer([
                            { id: 110, name: "South King County Fund 2022-25" },
                        ]);
                    }
                });
            },
            {
                root: null,
                threshold: 1,
            }
        );

        const targets = [sectionRefs.current[0]];

        targets.forEach((el) => {
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    // 3. Handle partnerIndex deep link
    useEffect(() => {
        if (partnerIndex == null) return;
        if (!Number.isFinite(partnerIndex)) return;
        if (partnerIndex < 0 || partnerIndex >= partners.length) return;

        setSelectedIndex(partnerIndex);

        setSearchParams({}, { replace: true });
    }, [partnerIndex, setSearchParams]);

    // 4. Scroll after selected partner opens
    useEffect(() => {
        if (selectedIndex === null) return;

        requestAnimationFrame(() => {
            if (!detailRef.current) return;

            const top =
                detailRef.current.getBoundingClientRect().top + window.scrollY - 96;

            window.scrollTo({ top, behavior: "smooth" });
        });
    }, [selectedIndex]);
    
    return (
        <div className="w-full bg-transparent no-scrollbar helvetica-page mb-52">
            {/* Main Content */}
            <div className="mx-auto w-full pl-6 py-2 md:pl-12 lg:pl-20">

                <div className="relative">
                    {/* ABSOLUTE RAIL OVERLAY */}
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute z-20 inset-y-0 left-0 w-full"
                    >
                        <div className="relative h-full pl-6 py-2 md:pl-12 lg:pl-20">
                            {/* Anchor rail at the left gutter */}
                            <div className="absolute left-0 top-24 bottom-0">
                                {/* Rail column width */}
                                <div className="w-16 h-full flex flex-col">
                                    {/* Vertical arrow */}
                                    <div className="mt-2 flex flex-1 flex-col items-start">
                                        {/* Vertical line */}
                                        <div className="ml-[calc(50%-2px)] w-[6px] flex-1 bg-port-blue rounded-t-full" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <section ref={(el) => (sectionRefs.current[0] = el)}>
                        <div className='bg-white pt-2 shadow-xl'>
                            <div className='flex items-center pl-2 pt-2 bg-white'>
                                <div className="flex justify-center">
                                    <img src={pinIcon} alt="" className="w-12 h-auto" />
                                </div>
                                <h1 className="text-3xl font-extrabold text-port-blue md:text-4xl lg:text-5xl ml-6">
                                    Our Partner Stories
                                </h1>
                            </div>
                            <div className="pl-20 bg-white pr-8">
                                <p className="mb-4 max-w-4xl text-sm md:text-lg">
                                    Our partners are a diverse group of community-based organizations, nonprofits, and chambers of commerce rooted in South King County. Our partners understand their communities because they’re part of them—bringing lived experience, care, and insight into everything they do. Together, they help create programs that reflect real needs, open doors to new opportunities, and support a future where everyone has a chance to thrive. Select the partners below to learn more.
                                </p>
                            </div>

                            {/* Partner Image Buttons */}
                            <div className="py-8 px-10 bg-white mb-12 mx-auto shadow-xl">
                                <div className='-mt-8 pb-4 pl-10'>
                                    <PageDots
                                        count={5}
                                        color="#21506F"
                                        currentIndex={0}
                                        sectionRefs={sectionRefs}
                                    />
                                </div>
                                <div
                                    className="grid gap-4 pt-4 ml-10"
                                    style={{
                                        gridTemplateColumns: `repeat(${Math.min(partners.length, 4)}, minmax(0, 1fr))`,
                                    }}
                                >
                                    {partners.map(({ src, alt, color, title }, i) => {
                                        const vHeight = 140;
                                        const stroke = 6;
                                        const isSelected = selectedIndex === i;

                                        return (
                                            <div key={i} className="group relative flex flex-col items-center w-full">
                                                <button
                                                    type="button"
                                                    onClick={() => handleSelectProgram(i)}
                                                    aria-pressed={isSelected}
                                                    className={[
                                                        "relative z-10 w-full h-full flex flex-col items-center gap-2 rounded-xl px-3 pt-4 pb-3",
                                                        "border-2 transition-all duration-200",
                                                        "hover:-translate-y-1 hover:shadow-md",
                                                        isSelected
                                                            ? "shadow-md -translate-y-1"
                                                            : "bg-white border-slate-200 shadow-sm",
                                                    ].join(" ")}
                                                    style={{
                                                        borderColor: isSelected ? color : undefined,
                                                        backgroundColor: isSelected ? `${color}12` : undefined,
                                                    }}
                                                >
                                                    {/* Logo */}
                                                    <div className="relative flex items-center justify-center w-14 h-14 md:w-20 md:h-20 flex-shrink-0">
                                                        {/* Ring on top */}
                                                        <div
                                                            className="absolute inset-0 rounded-full z-10 pointer-events-none"
                                                            style={{ boxShadow: `0 0 0 3px ${color}` }}
                                                        />
                                                        <div className="w-full h-full rounded-full bg-white overflow-hidden p-1.5">
                                                            <img src={src} alt={alt} className="w-full h-full object-contain rounded-xl" />
                                                        </div>
                                                    </div>

                                                    {/* Partner name — grows to fill, stays centered vertically */}
                                                    <span
                                                        className="text-xs md:text-sm font-bold text-center leading-tight flex-1 flex items-center justify-center"
                                                        style={{ color: isSelected ? color : "#1a3a5c" }}
                                                    >
                                                        {title ?? alt}
                                                    </span>

                                                    {/* Colored bottom accent bar */}
                                                    <div
                                                        className="absolute bottom-0 left-0 right-0 h-1 rounded-b-xl transition-opacity duration-200"
                                                        style={{
                                                            backgroundColor: color,
                                                            opacity: isSelected ? 1 : 0.3,
                                                        }}
                                                    />
                                                </button>

                                                {/* Connector lines — anchored to bottom of outer div, not button */}
                                                <div className="pointer-events-none absolute left-0 right-0 top-full mt-1">
                                                    {/* Vertical segment */}
                                                    <div
                                                        className={[
                                                            "mx-auto origin-top transition-transform duration-300 ease-out",
                                                            isSelected ? "scale-y-100" : "scale-y-0 group-hover:scale-y-100",
                                                        ].join(" ")}
                                                        style={{ width: stroke, height: vHeight, backgroundColor: color }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div ref={detailRef} className='scroll-mt-24 relative bg-white z-30'>
                            {selectedProgram &&
                                <ProgramDetailCard
                                    program={selectedProgram}
                                    programs={partners}
                                    selectedIndex={selectedIndex}
                                    onSelectProgram={handleSelectProgram}
                                />
                            }
                        </div>
                    </section>

                    {/* Transparent spacer */}
                    <TransparentSpacer height={"50"} />

                    <section ref={(el) => (sectionRefs.current[1] = el)} className="scroll-mt-24 relative z-10">
                        <div className="pl-20 pr-8 bg-white pt-2 shadow-xl">
                            <h1 className="text-3xl font-extrabold text-port-blue md:text-4xl lg:text-5xl">
                                Why South King County?
                            </h1>
                            <p className="py-4 max-w-4xl text-sm md:text-lg">
                                South King County is one of the most ethnically and culturally diverse communities in Washington State. Over two-fifths of households speak one or more languages other than English at home. Over three-fifths of residents are people of color, and a quarter of the population are children. South King County has the largest share of residents and collectively some of the lowest equity scores in all of King County. In response, the Port has designed multiple programs to address disparities in the region to advance equity, sustainability, and prosperity for all.
                            </p>

                            <div className="border-b-2 border-black">
                                <h2 className='font-bold text-xl md:text-2xl'>Equity Index Scores for South King County</h2>
                                <p className='max-w-4xl text-sm md:text-lg'>Select each of the indicators below to see the index score.</p>
                            </div>
                        </div>

                        <div className='bg-white pt-2 shadow-xl'>
                            <IndexList />

                            <div className='py-4 pl-20 pr-8 mt-4'>
                                <div className="border-b-2 border-black">
                                    <h2 className='font-bold text-xl md:text-2xl'>About South King and Port Communities Fund</h2>
                                    <p className='max-w-4xl text-sm md:text-lg'>Discover more about SKCPF on our Port Website.</p>
                                </div>
                                {/* Card Grid */}
                                <LinkCardGrid cards={cards} />

                                <PageDots
                                    count={5}
                                    color="#21506F"
                                    currentIndex={1}
                                    sectionRefs={sectionRefs}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Transparent spacer */}
                    <TransparentSpacer height={"50"} />

                    {/* Program Priorities section */}
                    <section className='bg-white shadow-xl scroll-mt-24'>
                        <div ref={(el) => (sectionRefs.current[2] = el)} >
                            <WorkforceImpactCard />
                            <div className='bg-white pl-20 pb-4'>
                                <PageDots
                                    count={5}
                                    color="#21506F"
                                    currentIndex={2}
                                    sectionRefs={sectionRefs}
                                />
                            </div>
                        </div>
                        <div ref={(el) => (sectionRefs.current[3] = el)} >
                            <EnvironmentImpactCard />
                            <div className='bg-white pl-20 pb-4'>
                                <PageDots
                                    count={5}
                                    color="#21506F"
                                    currentIndex={3}
                                    sectionRefs={sectionRefs}
                                />
                            </div>
                        </div>
                        <div ref={(el) => (sectionRefs.current[4] = el)} ></div>
                        <EconomicImpactCard />
                        <div className='bg-white pl-20 pb-4'>
                            <PageDots
                                count={5}
                                color="#21506F"
                                currentIndex={4}
                                sectionRefs={sectionRefs}
                            />
                        </div>
                    </section>

                    <TransparentSpacer height={"50"} />

                    {/* Footer (end section) */}
                    {/* <section ref={(el) => (sectionRefs.current[5] = el)} className="relative scroll-mt-24 shadow-xl mb-8">
                        <h1 className="pl-20 pr-8 pb-2 bg-white/60 backdrop-blur-sm text-3xl font-extrabold text-port-blue md:text-4xl lg:text-5xl">
                            Learn More About the Port's Community Impact
                        </h1>

                        <div className='bg-white pl-20 py-2'>
                            <PageDots
                                count={6}
                                color="#21506F"
                                currentIndex={5}
                                sectionRefs={sectionRefs}
                            />
                        </div>

                        <div className="relative w-full h-[350px] overflow-hidden">
                            <img
                                src={portBGImg}
                                alt="fish monitoring impacts"
                                className="absolute inset-0 w-full h-full object-cover"
                            />

                            <div className='absolute bottom-4 left-0 px-12'>
                                <LinkCardGrid cards={footerCards} />
                            </div>
                        </div>
                    </section> */}
                    <StorymapFooter/>
                </div>
            </div>
        </div>
    );
}

export default StorymapPage